import {
  CheckCircle,
  CloudUploadOutlined,
  Delete,
  ErrorOutline,
  InsertDriveFileSharp,
} from "@mui/icons-material";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import type { ChangeEvent, DragEvent, MouseEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import * as XLSX from "xlsx";

import CrmRowActionMenu from "../../components/crm/CrmRowActionMenu";
import CrmTableToolbar from "../../components/crm/CrmTableToolbar";
import EmptyState from "../../components/crm/EmptyState";
import TableLoadingSkeleton, {
  type TableSkeletonColumn,
} from "../../components/crm/TableLoadingSkeleton";
import CustomConfirmDialog from "../../components/CustomConfirmDialog";
import CustomDialog from "../../components/CustomDialog";
import { useBreadcrumbs } from "../../contexts/BreadcrumbContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import useCrmProductAddons from "../../hooks/useCrmProductAddons";
import {
  deleteAdminProductAddon,
  uploadProductAddons,
} from "../../services/order.service";
import {
  ProductAddon,
  ProductAddonFilters,
} from "../../types/order";

type UploadState = "idle" | "uploading" | "success" | "failed";
type UploadStatus = { state: UploadState; message?: string };

const MAX_IMPORT_FILE_BYTES = 4 * 1024 * 1024;
const VALID_IMPORT_EXT_RE = /\.(xlsx|xls|csv)$/i;
const fileKey = (f: File) => `${f.name}-${f.size}-${f.lastModified}`;

const REQUIRED_IMPORT_COLUMNS = [
  "product_type_cd",
  "commercial_product_cd",
  "custom_product_desc",
  "custom_product_search_desc",
  "legalentity_ext_2",
  "legalentity_ext_3",
  "legalentity_ext_4",
  "legalentity_ext_5",
  "legalentity_ext_6",
  "legalentity_ext_7",
];

type ProductAddonListFilters = ProductAddonFilters;

const tableColumns = [
  { key: "actions", label: "Actions", width: 80 },
  { key: "no", label: "No.", width: 80 },
  { key: "productType", label: "Product Type CD", width: 158 },
  { key: "commercialCode", label: "Commercial Product CD", width: 220 },
  { key: "customDesc", label: "Custom Product Desc", width: 246 },
  { key: "customSearch", label: "Custom Product Search Desc", width: 256 },
  { key: "ext1", label: "LegalEntity Ext 1", width: 168 },
  { key: "ext2", label: "LegalEntity Ext 2", width: 168 },
  { key: "ext3", label: "LegalEntity Ext 3", width: 168 },
  { key: "ext4", label: "LegalEntity Ext 4", width: 168 },
  { key: "ext5", label: "LegalEntity Ext 5", width: 168 },
  { key: "ext6", label: "LegalEntity Ext 6", width: 168 },
  { key: "ext7", label: "LegalEntity Ext 7", width: 168 },
] as const;

const formatProductType = (type?: string) => type || "-";

const getLegalEntityValue = (
  addon: ProductAddon,
  extIndex: 1 | 2 | 3 | 4 | 5 | 6 | 7,
) => {
  const entity = addon.legal_entity;
  if (!entity) return "-";

  const candidates = [
    `legal_entity_ext${extIndex}`,
    `ext${extIndex}`,
    `ext_${extIndex}`,
    `legalentity_ext_${extIndex}`,
    `legalEntityExt${extIndex}`,
    `${extIndex}`,
  ];

  for (const key of candidates) {
    if (entity[key]) return entity[key];
  }

  return "-";
};

export default function CrmAddons() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    setBreadcrumbs([{ label: "Add-on List", to: "/crm/addons" }]);
  }, [setBreadcrumbs]);

  const [filters, setFilters] = useState<ProductAddonListFilters>({
    search: "",
    page: 0,
    page_size: 10,
  });

  const { data, dataLoading, mutate } = useCrmProductAddons(true, filters);
  const addons = useMemo<ProductAddon[]>(() => data?.data || [], [data?.data]);
  const totalRecords = data?.total_items || 0;

  const skeletonColumns = useMemo<TableSkeletonColumn[]>(
    () => [
      { key: "checkbox", isCheckbox: true },
      ...tableColumns.map((column) => ({
        key: column.key,
        width: column.width,
      })),
    ],
    [],
  );

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [selectedAddon, setSelectedAddon] = useState<ProductAddon | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [actionAnchorEl, setActionAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [actionRow, setActionRow] = useState<ProductAddon | null>(null);

  const handleSearchChange = useCallback((value: string) => {
    setFilters((prev) => ({ ...prev, search: value, page: 0 }));
    setSelectedIds([]);
  }, []);

  const handlePageChange = useCallback((_: unknown, page: number) => {
    setFilters((prev) => ({ ...prev, page }));
    setSelectedIds([]);
  }, []);

  const handlePageSizeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setFilters((prev) => ({
        ...prev,
        page_size: parseInt(event.target.value, 10),
        page: 0,
      }));
      setSelectedIds([]);
    },
    [],
  );

  const handleAddNew = () => {
    navigate("/crm/addons/create");
  };

  const handleEdit = (addon: ProductAddon) => {
    if (!addon.id) return;
    navigate(`/crm/addons/${addon.id}/edit`);
  };

  const handleDeleteClick = (addon: ProductAddon) => {
    setSelectedAddon(addon);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedAddon?.id) return;
    setIsSubmitting(true);
    try {
      await deleteAdminProductAddon(selectedAddon.id);
      setDeleteDialogOpen(false);
      setSelectedAddon(null);
      mutate();
      showSnackbar("Add-on deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting product add-on:", error);
      showSnackbar("Failed to delete add-on", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setIsSubmitting(true);
    try {
      await Promise.all(
        selectedIds.map((id) => deleteAdminProductAddon(id)),
      );
      showSnackbar(
        `Successfully deleted ${selectedIds.length} add-on(s).`,
        "success",
      );
      setBulkDeleteDialogOpen(false);
      setSelectedIds([]);
      mutate();
    } catch (error) {
      console.error("Error deleting add-ons:", error);
      showSnackbar("Failed to delete some add-ons.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const allSelected =
    addons.length > 0 &&
    addons.every((row) => row.id && selectedIds.includes(row.id));
  const isIndeterminate =
    selectedIds.length > 0 && !allSelected && addons.length > 0;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(addons.map((row) => row.id || "").filter(Boolean));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectRow = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  };

  const handleOpenActionMenu = (
    addon: ProductAddon,
    event: MouseEvent<HTMLElement>,
  ) => {
    setActionAnchorEl(event.currentTarget);
    setActionRow(addon);
  };

  const handleCloseActionMenu = () => {
    setActionAnchorEl(null);
    setActionRow(null);
  };

  const handleActionEdit = () => {
    if (actionRow) handleEdit(actionRow);
    handleCloseActionMenu();
  };

  const handleActionDelete = () => {
    if (actionRow) handleDeleteClick(actionRow);
    handleCloseActionMenu();
  };

  // ── Import state & handlers ──
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFiles, setImportFiles] = useState<File[]>([]);
  const [importStatuses, setImportStatuses] = useState<UploadStatus[]>([]);
  const [importUploadingIndex, setImportUploadingIndex] = useState<
    number | null
  >(null);
  const [isImportDragActive, setIsImportDragActive] = useState(false);
  const importFileInputRef = useRef<HTMLInputElement>(null);

  const handleOpenImportDialog = () => setImportDialogOpen(true);

  const handleCloseImportDialog = () => {
    if (isSubmitting) return;
    setImportDialogOpen(false);
    setIsImportDragActive(false);
    setImportFiles([]);
    setImportStatuses([]);
    setImportUploadingIndex(null);
  };

  const addFilesToImport = (incoming: File[]) => {
    if (incoming.length === 0) return;
    const rejected: string[] = [];
    const existingKeys = new Set(importFiles.map(fileKey));
    const accepted: File[] = [];
    for (const file of incoming) {
      if (!VALID_IMPORT_EXT_RE.test(file.name)) {
        rejected.push(`${file.name} (unsupported type)`);
        continue;
      }
      if (file.size > MAX_IMPORT_FILE_BYTES) {
        rejected.push(`${file.name} (exceeds 4 MB)`);
        continue;
      }
      const key = fileKey(file);
      if (existingKeys.has(key)) continue;
      existingKeys.add(key);
      accepted.push(file);
    }
    if (rejected.length > 0) {
      showSnackbar(`Some files were skipped: ${rejected.join(", ")}`, "error");
    }
    if (accepted.length === 0) return;
    const merged = [...importFiles, ...accepted].slice(0, 100);
    setImportFiles(merged);
    setImportStatuses(merged.map(() => ({ state: "idle" as UploadState })));
  };

  const handleImportFileInputChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    addFilesToImport(Array.from(event.target.files || []));
    event.target.value = "";
  };

  const handleImportDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (isSubmitting) return;
    setIsImportDragActive(true);
  };

  const handleImportDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsImportDragActive(false);
  };

  const handleImportDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsImportDragActive(false);
    if (isSubmitting) return;
    addFilesToImport(Array.from(event.dataTransfer.files || []));
  };

  const handleDeleteImportFile = (index: number) => {
    if (isSubmitting) return;
    setImportFiles((prev) => prev.filter((_, i) => i !== index));
    setImportStatuses((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes >= 1024 * 1024) {
      const mb = Math.round((bytes / (1024 * 1024)) * 10) / 10;
      return `${Number.isInteger(mb) ? mb.toFixed(0) : mb.toFixed(1)} MB`;
    }
    return `${Math.round(bytes / 1024)} KB`;
  };

  const SAMPLE_ADDON_ROWS = [
    REQUIRED_IMPORT_COLUMNS,
    [
      "VALUE_ADD",
      "ADDON001",
      "Blue Light Filter Lens",
      "blue_light_filter",
      "",
      "",
      "",
      "",
      "",
      "",
    ],
    [
      "INSTRUCTION",
      "ADDON002",
      "UV Protection Premium",
      "uv_protection",
      "",
      "",
      "",
      "",
      "",
      "",
    ],
  ];

  const handleDownloadSampleXlsx = () => {
    const worksheet = XLSX.utils.aoa_to_sheet(SAMPLE_ADDON_ROWS);
    worksheet["!cols"] = SAMPLE_ADDON_ROWS[0].map(() => ({ wch: 24 }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Addons");
    XLSX.writeFile(workbook, "sample_import_addons.xlsx");
  };

  const handleDownloadSampleCsv = () => {
    const worksheet = XLSX.utils.aoa_to_sheet(SAMPLE_ADDON_ROWS);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "sample_import_addons.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const updateImportStatus = (index: number, status: UploadStatus) => {
    setImportStatuses((prev) => {
      const next = prev.slice();
      next[index] = status;
      return next;
    });
  };

  const uploadFilesSequentially = async (indices: number[]) => {
    let created = 0;
    let updated = 0;
    let total = 0;
    let successCount = 0;
    let failCount = 0;

    for (const i of indices) {
      setImportUploadingIndex(i);
      updateImportStatus(i, { state: "uploading" });
      try {
        const formData = new FormData();
        formData.append("file", importFiles[i]);
        const result = await uploadProductAddons(formData);
        created += result?.created ?? 0;
        updated += result?.updated ?? 0;
        total += result?.total ?? 0;
        successCount += 1;
        updateImportStatus(i, { state: "success" });
      } catch (err: unknown) {
        failCount += 1;
        const msg = err instanceof Error ? err.message : "Upload failed.";
        updateImportStatus(i, { state: "failed", message: msg });
      }
    }

    setImportUploadingIndex(null);
    return { created, updated, total, successCount, failCount };
  };

  const handleSubmitImportData = async () => {
    if (importFiles.length === 0 || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const targets = importStatuses.some((s) => s?.state === "failed")
        ? importStatuses
            .map((s, i) => (s?.state === "failed" ? i : -1))
            .filter((i) => i >= 0)
        : importFiles.map((_, i) => i);

      const { created, updated, total, successCount, failCount } =
        await uploadFilesSequentially(targets);

      if (failCount === 0) {
        showSnackbar(
          `Import completed: ${created} created, ${updated} updated, ${total} total.`,
          "success",
        );
        setImportDialogOpen(false);
        setImportFiles([]);
        setImportStatuses([]);
        setImportUploadingIndex(null);
        mutate();
      } else {
        showSnackbar(
          `Imported ${successCount} of ${targets.length} file(s). ${failCount} failed — review and retry.`,
          "error",
        );
        if (successCount > 0) mutate();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Paper sx={{ borderRadius: 0, overflow: "hidden" }}>
        <CrmTableToolbar
          searchValue={filters.search || ""}
          onSearchChange={handleSearchChange}
          onCreate={handleAddNew}
          createLabel="Create Add On"
          importLabel="Import Data"
          showImport
          onImport={handleOpenImportDialog}
          rightExtra={
            selectedIds.length > 0 ? (
              <Button
                variant="contained"
                color="error"
                startIcon={<Delete />}
                onClick={() => setBulkDeleteDialogOpen(true)}
                sx={{
                  borderRadius: "999px",
                  px: 2.25,
                  textTransform: "none",
                  minHeight: 40,
                  boxShadow: "none",
                  "&:hover": { boxShadow: "none" },
                }}
              >
                Delete Selected ({selectedIds.length})
              </Button>
            ) : undefined
          }
        />

        {dataLoading ? (
          <TableLoadingSkeleton
            columns={skeletonColumns}
            rowCount={10}
            minWidth={2290}
          />
        ) : addons.length > 0 ? (
          <>
            <TableContainer
              sx={{ borderTop: `1px solid ${theme.vars.palette.divider}` }}
            >
              <Table sx={{ minWidth: 2290 }}>
                <TableHead>
                  <TableRow
                    sx={{
                      "& .MuiTableCell-root": {
                        height: 52,
                        borderBottom: `1px solid ${theme.vars.palette.divider}`,
                        color: "#25272C",
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        lineHeight: 1.4,
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <TableCell padding="checkbox" sx={{ width: 74, px: 2 }}>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={isIndeterminate}
                        onChange={(event) =>
                          handleSelectAll(event.target.checked)
                        }
                        size="small"
                        sx={{ '&.Mui-checked': { color: '#1976D2' }, '&.MuiCheckbox-indeterminate': { color: '#1976D2' } }}
                      />
                    </TableCell>
                    {tableColumns.map((column) => (
                      <TableCell
                        key={column.key}
                        sx={{ width: column.width, px: 2 }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {addons.map((addon, index) => (
                    <TableRow
                      key={addon.id || `${addon.index_key}-${index}`}
                      selected={addon.id ? selectedIds.includes(addon.id) : false}
                      sx={{
                        "&.Mui-selected, &.Mui-selected:hover": {
                          backgroundColor: "#F9FAFB",
                        },
                        "& .MuiTableCell-root": {
                          height: 79,
                          borderBottom: `1px solid ${theme.vars.palette.divider}`,
                          fontSize: 12,
                          color: "#25272C",
                          whiteSpace: "nowrap",
                        },
                      }}
                    >
                      <TableCell padding="checkbox" sx={{ width: 74, px: 2 }}>
                        <Checkbox
                          checked={
                            addon.id ? selectedIds.includes(addon.id) : false
                          }
                          onChange={() => addon.id && handleSelectRow(addon.id)}
                          disabled={!addon.id}
                          size="small"
                          sx={{ '&.Mui-checked': { color: '#1976D2' } }}
                        />
                      </TableCell>

                      <TableCell sx={{ width: 80, px: 2 }}>
                        <IconButton
                          size="small"
                          onClick={(event) =>
                            handleOpenActionMenu(addon, event)
                          }
                        >
                          <MoreVertOutlinedIcon fontSize="small" />
                        </IconButton>
                      </TableCell>

                      <TableCell sx={{ width: 80, px: 2 }}>
                        {(filters.page || 0) * (filters.page_size || 10) +
                          index +
                          1}
                      </TableCell>
                      <TableCell sx={{ width: 158, px: 2 }}>
                        {formatProductType(addon.type)}
                      </TableCell>
                      <TableCell sx={{ width: 220, px: 2 }}>
                        {addon.index_key || "-"}
                      </TableCell>
                      <TableCell sx={{ width: 246, px: 2 }}>
                        {addon.custom_product || "-"}
                      </TableCell>
                      <TableCell sx={{ width: 256, px: 2 }}>
                        {addon.custom_product_search || "-"}
                      </TableCell>
                      <TableCell sx={{ width: 168, px: 2 }}>
                        {getLegalEntityValue(addon, 1)}
                      </TableCell>
                      <TableCell sx={{ width: 168, px: 2 }}>
                        {getLegalEntityValue(addon, 2)}
                      </TableCell>
                      <TableCell sx={{ width: 168, px: 2 }}>
                        {getLegalEntityValue(addon, 3)}
                      </TableCell>
                      <TableCell sx={{ width: 168, px: 2 }}>
                        {getLegalEntityValue(addon, 4)}
                      </TableCell>
                      <TableCell sx={{ width: 168, px: 2 }}>
                        {getLegalEntityValue(addon, 5)}
                      </TableCell>
                      <TableCell sx={{ width: 168, px: 2 }}>
                        {getLegalEntityValue(addon, 6)}
                      </TableCell>
                      <TableCell sx={{ width: 168, px: 2 }}>
                        {getLegalEntityValue(addon, 7)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalRecords}
              page={filters.page || 0}
              onPageChange={handlePageChange}
              rowsPerPage={filters.page_size || 10}
              onRowsPerPageChange={handlePageSizeChange}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Rows per page:"
              sx={{
                "& .MuiTablePagination-toolbar": {
                  minHeight: 44,
                  px: 2,
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                  {
                    fontSize: 12,
                    color: "#25272C",
                  },
              }}
            />
          </>
        ) : (
          <EmptyState
            variant="addons"
            title="No add-ons found"
            description="Start by adding your first add-on to the CRM."
            actionLabel="Create Add On"
            onAction={handleAddNew}
          />
        )}
      </Paper>

      <CrmRowActionMenu
        anchorEl={actionAnchorEl}
        onClose={handleCloseActionMenu}
        onEdit={handleActionEdit}
        onDelete={handleActionDelete}
      />

      <CustomDialog
        open={importDialogOpen}
        title="Import Data"
        onClose={handleCloseImportDialog}
        maxWidth="sm"
        titleStyle={{
          fontSize: 20,
          fontWeight: 600,
          lineHeight: 1.4,
          color: "#25272C",
        }}
        PaperProps={{
          sx: {
            borderRadius: "4px",
            width: 552,
            maxWidth: "calc(100% - 32px)",
          },
        }}
        content={
          <Stack spacing={2}>
            <Typography
              sx={{
                fontSize: 12,
                lineHeight: 1.4,
                color: "#8E95A2",
              }}
            >
              Required columns: {REQUIRED_IMPORT_COLUMNS.join(", ")}
            </Typography>
            <Stack spacing={0.5}>
              <Typography
                sx={{
                  fontSize: 14,
                  lineHeight: 1.4,
                  color: "#8E95A2",
                }}
              >
                You can download a sample file to view the required data format
              </Typography>
              <Stack direction="row" spacing={1}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.4,
                    color: "#0054FC",
                  }}
                  onClick={handleDownloadSampleXlsx}
                >
                  Download sample (.xlsx)
                </Link>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.4,
                    color: "#0054FC",
                  }}
                  onClick={handleDownloadSampleCsv}
                >
                  Download sample (.csv)
                </Link>
              </Stack>
            </Stack>

            {importFiles.length > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: "6px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 16,
                    lineHeight: 1.4,
                    color: "#25272C",
                  }}
                >
                  Upload Add-On File
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<CloudUploadOutlined sx={{ fontSize: 20 }} />}
                  onClick={() => importFileInputRef.current?.click()}
                  disabled={isSubmitting}
                  sx={{
                    px: 2,
                    borderRadius: "100px",
                    minHeight: 32,
                    textTransform: "none",
                    fontSize: 16,
                    lineHeight: 1.4,
                    fontWeight: 400,
                    bgcolor: "#111111",
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#25272C", boxShadow: "none" },
                  }}
                >
                  Upload
                </Button>
              </Box>
            ) : (
              <Typography
                sx={{
                  py: "6px",
                  fontSize: 16,
                  lineHeight: 1.4,
                  color: "#25272C",
                }}
              >
                Attach Add-On File
              </Typography>
            )}

            <Box
              onDrop={handleImportDrop}
              onDragOver={handleImportDragOver}
              onDragLeave={handleImportDragLeave}
              sx={{
                height: 228,
                borderRadius: 1,
                bgcolor: isImportDragActive ? "#F7F8F8" : "#FFFFFF",
                overflowY: "auto",
                pointerEvents: isSubmitting ? "none" : "auto",
              }}
            >
              {importFiles.length > 0 ? (
                <Stack spacing={1}>
                  {importFiles.map((file, index) => {
                    const status = importStatuses[index]?.state ?? "idle";
                    const errMsg = importStatuses[index]?.message;
                    const nameColor =
                      status === "failed" ? "#B3261E" : "#25272C";
                    return (
                      <Box
                        key={`${fileKey(file)}-${index}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          py: "6px",
                          opacity:
                            isSubmitting &&
                            status !== "uploading" &&
                            status !== "failed"
                              ? 0.6
                              : 1,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                          sx={{ minWidth: 0, flex: 1 }}
                        >
                          <InsertDriveFileSharp
                            sx={{ fontSize: 24, color: "#6B7280" }}
                          />
                          <Stack sx={{ minWidth: 0 }}>
                            <Typography
                              noWrap
                              sx={{
                                fontSize: 16,
                                lineHeight: 1.5,
                                letterSpacing: "0.15px",
                                color: nameColor,
                              }}
                            >
                              {file.name} ({formatFileSize(file.size)})
                            </Typography>
                            {status === "failed" && errMsg && (
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  lineHeight: 1.4,
                                  color: "#B3261E",
                                }}
                              >
                                {errMsg}
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                        >
                          {status === "uploading" && (
                            <CircularProgress
                              size={18}
                              sx={{ color: "#0054FC" }}
                            />
                          )}
                          {status === "success" && (
                            <Tooltip title="Uploaded">
                              <CheckCircle
                                sx={{ fontSize: 22, color: "#2E7D32" }}
                              />
                            </Tooltip>
                          )}
                          {status === "failed" && (
                            <Tooltip title={errMsg || "Upload failed"}>
                              <ErrorOutline
                                sx={{ fontSize: 22, color: "#B3261E" }}
                              />
                            </Tooltip>
                          )}
                          <IconButton
                            onClick={() => handleDeleteImportFile(index)}
                            disabled={isSubmitting}
                            sx={{ color: "#F44336" }}
                          >
                            <Delete />
                          </IconButton>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <Stack
                  sx={{
                    height: "100%",
                    border: "1px dashed #6B7280",
                    borderRadius: 1,
                    bgcolor: "#EDEEF1",
                    px: 2,
                    py: 2,
                  }}
                  alignItems="center"
                  justifyContent="center"
                  spacing={2}
                >
                  <Stack spacing={0.5} alignItems="center">
                    <Typography sx={{ color: "#5F6D78", fontSize: 16 }}>
                      Drag and drop file here or click
                    </Typography>
                    <Typography sx={{ color: "#9CADB6", fontSize: 14 }}>
                      Upload up to 100 files
                    </Typography>
                  </Stack>
                  <Button
                    variant="contained"
                    startIcon={<CloudUploadOutlined sx={{ fontSize: 24 }} />}
                    onClick={() => importFileInputRef.current?.click()}
                    disabled={isSubmitting}
                    sx={{
                      px: 3,
                      py: 1,
                      borderRadius: "96px",
                      minHeight: 40,
                      textTransform: "none",
                      fontSize: 16,
                      fontWeight: 700,
                      lineHeight: 1.5,
                      bgcolor: "#111111",
                      boxShadow:
                        "0px 1px 5px rgba(0, 0, 0, 0.12), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.2)",
                      "&:hover": { bgcolor: "#25272C" },
                    }}
                  >
                    Upload
                  </Button>
                </Stack>
              )}
            </Box>

            {importFiles.length > 0 &&
              (() => {
                const successCount = importStatuses.filter(
                  (s) => s?.state === "success",
                ).length;
                const failedCount = importStatuses.filter(
                  (s) => s?.state === "failed",
                ).length;
                const hasFailures = failedCount > 0;
                const doneCount = successCount + failedCount;
                const progressPct =
                  importFiles.length === 0
                    ? 0
                    : Math.round((doneCount / importFiles.length) * 100);
                const submitLabel = isSubmitting
                  ? `Uploading ${(importUploadingIndex ?? 0) + 1} of ${importFiles.length}...`
                  : hasFailures
                    ? `Retry ${failedCount} failed`
                    : "Import";
                return (
                  <>
                    {(isSubmitting || doneCount > 0) && (
                      <Stack spacing={0.75}>
                        <LinearProgress
                          variant="determinate"
                          value={isSubmitting ? progressPct : 100}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: "#EDEEF1",
                            "& .MuiLinearProgress-bar": {
                              bgcolor: hasFailures ? "#B3261E" : "#2E7D32",
                            },
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: 12,
                            lineHeight: 1.4,
                            color: "#5F6D78",
                          }}
                        >
                          {isSubmitting
                            ? `Uploading ${doneCount + 1} of ${importFiles.length}…`
                            : `Completed: ${successCount} succeeded${
                                failedCount > 0
                                  ? `, ${failedCount} failed`
                                  : ""
                              }`}
                        </Typography>
                      </Stack>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        py: "6px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 16,
                          lineHeight: 1.4,
                          color: "#25272C",
                        }}
                      >
                        Uploaded: {importFiles.length}/100
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 14,
                          lineHeight: 1.4,
                          color: "#8E95A2",
                        }}
                      >
                        .xlsx / .csv · max 4 MB per file
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        onClick={handleCloseImportDialog}
                        disabled={isSubmitting}
                        sx={{
                          borderRadius: "96px",
                          px: 3,
                          minHeight: 40,
                          textTransform: "none",
                          fontSize: 16,
                          fontWeight: 700,
                          lineHeight: 1.5,
                          borderColor: "#111111",
                          color: "#111111",
                          "&:hover": {
                            borderColor: "#111111",
                            bgcolor: "#F7F8F8",
                          },
                        }}
                      >
                        {hasFailures ? "Close" : "Cancel"}
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSubmitImportData}
                        disabled={importFiles.length === 0 || isSubmitting}
                        startIcon={
                          isSubmitting ? (
                            <CircularProgress
                              size={16}
                              sx={{ color: "#8E95A2" }}
                            />
                          ) : undefined
                        }
                        sx={{
                          borderRadius: "96px",
                          px: 3,
                          minHeight: 40,
                          textTransform: "none",
                          fontSize: 16,
                          fontWeight: 700,
                          lineHeight: 1.5,
                          bgcolor: "#111111",
                          boxShadow:
                            "0px 1px 5px rgba(0, 0, 0, 0.12), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.2)",
                          "&:hover": { bgcolor: "#25272C" },
                          "&.Mui-disabled": {
                            bgcolor: "#D8DBDF",
                            color: "#8E95A2",
                          },
                        }}
                      >
                        {submitLabel}
                      </Button>
                    </Stack>
                  </>
                );
              })()}

            <input
              ref={importFileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              multiple
              hidden
              onChange={handleImportFileInputChange}
            />
          </Stack>
        }
      />

      <CustomConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Delete Add-on"
        content={
          <Typography>
            Are you sure you want to delete this add-on? This action cannot be
            undone.
          </Typography>
        }
        action={
          <Stack direction="row" spacing={1}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleDelete}
              disabled={isSubmitting}
            >
              Delete
            </Button>
          </Stack>
        }
      />

      <CustomConfirmDialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        title="Delete Selected Add-ons"
        content={
          <Typography>
            Are you sure you want to delete {selectedIds.length} selected
            add-on(s)? This action cannot be undone.
          </Typography>
        }
        action={
          <Stack direction="row" spacing={1}>
            <Button
              onClick={() => setBulkDeleteDialogOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleBulkDelete}
              disabled={isSubmitting}
            >
              Delete
            </Button>
          </Stack>
        }
      />
    </Stack>
  );
}
